<!--
  Columns per period, stacked by series (like spending per group per month). Hover a column for
  the numbers. A legend appears for two or more series; colors follow the series, not its rank.
-->
<script lang="ts">
  import { compactMicro, formatMicro } from "../../lib/format.ts";

  export interface ColumnSeries {
    name: string;
    color: string;
    values: number[];
  }

  let {
    labels,
    series,
    height = 180,
    title,
    showTitle = true,
  }: { labels: string[]; series: ColumnSeries[]; height?: number; title: string; showTitle?: boolean } = $props();

  let width = $state(600);
  let hover = $state<number | null>(null);

  const pad = { top: 12, right: 8, bottom: 24, left: 52 };
  const totals = $derived(labels.map((_, i) => series.reduce((s, x) => s + Math.max(0, x.values[i] ?? 0), 0)));
  const max = $derived(Math.max(1, ...totals));
  const nice = $derived.by(() => {
    const step = 10 ** Math.floor(Math.log10(max / 3));
    const s = [1, 2, 2.5, 5, 10].map((m) => m * step).find((x) => max / x <= 4) ?? step * 10;
    const hi = Math.ceil(max / s) * s;
    const ticks: number[] = [];
    for (let v = 0; v <= hi + s / 2; v += s) ticks.push(v);
    return { hi, ticks };
  });
  const iw = $derived(Math.max(10, width - pad.left - pad.right));
  const ih = $derived(height - pad.top - pad.bottom);
  const slot = $derived(iw / Math.max(1, labels.length));
  const bw = $derived(Math.max(4, Math.min(36, slot * 0.62)));
  const y = (v: number) => pad.top + ih - (v / (nice.hi || 1)) * ih;
  const every = $derived(Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor(iw / 56)))));
</script>

<figure class="chart" bind:clientWidth={width}>
  {#if showTitle}<figcaption>{title}</figcaption>{/if}
  {#if labels.length && max > 1}
    <svg role="img" aria-label={title} {width} {height} onpointerleave={() => (hover = null)}>
      {#each nice.ticks as t, ti (ti)}
        <line class="grid" x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} />
        <text class="ylab" x={pad.left - 8} y={y(t)} dy="0.32em">{compactMicro(t)}</text>
      {/each}
      {#each labels as label, i (i)}
        {@const cx = pad.left + slot * i + slot / 2}
        {@const segs = series.map((s) => Math.max(0, s.values[i] ?? 0))}
        <g class:dim={hover !== null && hover !== i}>
          {#each segs as v, k (k)}
            {#if v > 0}
              {@const below = segs.slice(0, k).reduce((a, b) => a + b, 0)}
              {@const top = segs.slice(k + 1).every((w) => w <= 0)}
              {@const h = Math.max(1, y(below) - y(below + v) - (k > 0 ? 2 : 0))}
              <rect x={cx - bw / 2} y={y(below + v)} width={bw} height={h} rx={top ? 4 : 0} fill={series[k]!.color} />
              {#if top}<rect x={cx - bw / 2} y={y(below + v) + 4} width={bw} height={Math.max(0, h - 4)} fill={series[k]!.color} />{/if}
            {/if}
          {/each}
        </g>
        {#if i % every === 0 || i === labels.length - 1}
          <text class="xlab" x={cx} y={height - 6} text-anchor="middle">{label}</text>
        {/if}
        <rect class="hit" x={pad.left + slot * i} y={pad.top} width={slot} height={ih} onpointerenter={() => (hover = i)} onpointerdown={() => (hover = i)} role="presentation" />
      {/each}
    </svg>
    {#if hover !== null}
      <div class="tip" style:left="{Math.min(width - 170, Math.max(0, pad.left + slot * hover + slot / 2 - 80))}px">
        <strong>{labels[hover]}</strong>
        {#each series as s, si (si)}
          {#if (s.values[hover] ?? 0) > 0}<span><i style:background={s.color}></i>{s.name} <b class="num">{formatMicro(s.values[hover] ?? 0)}</b></span>{/if}
        {/each}
        {#if series.length > 1}<span class="tot">Total <b class="num">{formatMicro(totals[hover] ?? 0)}</b></span>{/if}
      </div>
    {/if}
    {#if series.length > 1}
      <div class="legend">
        {#each series as s, si (si)}<span><i style:background={s.color}></i>{s.name}</span>{/each}
      </div>
    {/if}
  {:else}
    <p class="muted empty">Nothing to show yet.</p>
  {/if}
</figure>

<style>
  .chart {
    position: relative;
    margin: 0;
    min-width: 0;
  }

  figcaption {
    font-weight: 650;
    margin-bottom: 4px;
  }

  svg {
    display: block;
    overflow: visible;
    touch-action: pan-y;
  }

  .grid {
    stroke: var(--rule);
  }

  .ylab,
  .xlab {
    fill: var(--muted);
    font-size: 11px;
  }

  .ylab {
    text-anchor: end;
  }

  g {
    transition: opacity 0.12s;
  }

  g.dim {
    opacity: 0.45;
  }

  .hit {
    fill: transparent;
  }

  .tip {
    position: absolute;
    top: 28px;
    display: grid;
    gap: 2px;
    min-width: 160px;
    padding: 8px 10px;
    border-radius: var(--r-md);
    background: var(--surface);
    box-shadow: var(--shadow-pop);
    font-size: var(--t-xs);
    pointer-events: none;
    z-index: 2;
  }

  .tip span {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .tip b {
    margin-left: auto;
  }

  .tip .tot {
    border-top: 1px solid var(--rule);
    padding-top: 2px;
  }

  i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 6px;
    font-size: var(--t-xs);
    color: var(--ink-2);
  }

  .legend span {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }

  .empty {
    padding: 24px 0;
  }
</style>
